/**
 * Fetchers that turn a {@link ServerListRequest} into an API call.
 *
 * A register's page supplies an endpoint path; these build the params, make the
 * call and unwrap the page. Written once so no page hand-rolls the query
 * string — which is the only way `toListParams` stays the single place the wire
 * format is written.
 */

import { api } from '../../lib/api-client';
import {
  toListParams,
  type FacetOption,
  type ServerListRequest,
  type ServerListResult,
} from './types';

/**
 * A server-side list fetcher for an endpoint.
 *
 * ```ts
 * const fetchRows = createServerFetcher<ClientRow>('/crm/clients');
 * ```
 *
 * `extraParams` carries anything outside the table's own state — a parent
 * record's id for a nested register. It must also appear in the table's
 * `queryKey`, or the cache will serve one parent's rows for another.
 */
export function createServerFetcher<T>(
  path: string,
  extraParams?: Record<string, string | number | undefined>,
): (request: ServerListRequest) => Promise<ServerListResult<T>> {
  return async (request) => {
    const page = await api.page<T>(path, {
      params: { ...toListParams(request), ...extraParams },
    });
    return { rows: page.rows, total: page.total };
  };
}

/**
 * A facets fetcher for the same endpoint.
 *
 * The endpoint is `<path>/facets`, which the API declares **before**
 * `/{public_id}` — declared after, the dynamic route swallows `facets` as an id
 * and this returns a 404 that the filter dropdown reads as "no options".
 */
export function createFacetsFetcher(
  path: string,
  extraParams?: Record<string, string | number | undefined>,
): (field: string, request: ServerListRequest) => Promise<FacetOption[]> {
  return async (field, request) => {
    return api.get<FacetOption[]>(`${path}/facets`, {
      params: { ...toListParams(request), field, ...extraParams },
    });
  };
}

/**
 * A fetcher over an array already in memory.
 *
 * For a genuinely bounded set — the roles, the services, one project's
 * milestones — where a round trip per keystroke buys nothing. It implements the
 * same contract, so a register can start here and move to the server later
 * without the page changing.
 *
 * **Not for anything a user can grow.** The moment the set outgrows a page this
 * sorts and filters only what it holds, which is wrong in a way nothing on
 * screen reveals.
 */
export function createClientFetcher<T extends Record<string, unknown>>(
  rows: readonly T[],
  searchFields: readonly (keyof T & string)[],
): (request: ServerListRequest) => Promise<ServerListResult<T>> {
  return async (request) => {
    let working = [...rows];

    if (request.q) {
      const needle = request.q.toLowerCase();
      working = working.filter((row) =>
        searchFields.some((field) => String(row[field] ?? '').toLowerCase().includes(needle)),
      );
    }

    for (const [field, filter] of Object.entries(request.filters)) {
      working = working.filter((row) => matches(row[field], filter));
    }

    for (const criterion of [...request.sort].reverse()) {
      working.sort((left, right) => {
        const a = left[criterion.field];
        const b = right[criterion.field];
        const order = compare(a, b);
        return criterion.dir === 'desc' ? -order : order;
      });
    }

    const total = working.length;
    const start = (request.page - 1) * request.pageSize;
    return { rows: working.slice(start, start + request.pageSize), total };
  };
}

function compare(a: unknown, b: unknown): number {
  // Blanks sort last in both directions. A column of mostly-empty values that
  // puts the blanks first when ascending hides the rows somebody is looking for.
  if (a === null || a === undefined || a === '') return b === null || b === undefined || b === '' ? 0 : 1;
  if (b === null || b === undefined || b === '') return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (typeof a === 'boolean' && typeof b === 'boolean') return Number(a) - Number(b);
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

function matches(value: unknown, filter: ServerListRequest['filters'][string]): boolean {
  const text = String(value ?? '').toLowerCase();
  switch (filter.type) {
    case 'string':
      return filter.conditions.every((condition) => {
        const needle = condition.value.toLowerCase();
        switch (condition.mode) {
          case 'contains':
            return text.includes(needle);
          case 'not_contains':
            return !text.includes(needle);
          case 'equals':
            return text === needle;
          case 'not_equals':
            return text !== needle;
          case 'starts':
            return text.startsWith(needle);
          case 'not_starts':
            return !text.startsWith(needle);
          case 'ends':
            return text.endsWith(needle);
          case 'not_ends':
            return !text.endsWith(needle);
          case 'regex':
            try {
              return new RegExp(condition.value, 'i').test(text);
            } catch {
              // An incomplete pattern while somebody is still typing must not
              // throw. It matches nothing until it is valid.
              return false;
            }
          case 'blank':
            return text === '';
          case 'not_blank':
            return text !== '';
        }
      });
    case 'select':
      return filter.values.length === 0 || filter.values.includes(String(value ?? ''));
    case 'boolean':
      return Boolean(value) === filter.value;
    case 'number-range': {
      const numeric = Number(value);
      if (Number.isNaN(numeric)) return false;
      if (filter.min !== undefined && numeric < filter.min) return false;
      if (filter.max !== undefined && numeric > filter.max) return false;
      return true;
    }
    case 'date-range': {
      const time = new Date(String(value ?? '')).getTime();
      if (Number.isNaN(time)) return false;
      if (filter.from && time < new Date(filter.from).getTime()) return false;
      // The upper bound covers the whole day, matching the server.
      if (filter.to && time > new Date(`${filter.to}T23:59:59.999`).getTime()) return false;
      return true;
    }
  }
}

/** Facet counts over an in-memory array, to pair with {@link createClientFetcher}. */
export function createClientFacets<T extends Record<string, unknown>>(
  rows: readonly T[],
): (field: string) => Promise<FacetOption[]> {
  return async (field) => {
    const counts = new Map<string, number>();
    for (const row of rows) {
      const value = row[field];
      if (value === null || value === undefined || value === '') continue;
      const key = String(value);
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return Array.from(counts.entries())
      .map(([value, count]) => ({ value, count }))
      .sort((a, b) => b.count - a.count);
  };
}
